/**
 * GuruDetailScreen
 * 
 * Shows a guru's recommended books with their profile
 */

import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Image,
  Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme } from '../../../components/ui';
import { FireBook } from '../../../models/FirestoreModels';
import { BookService } from '../../../services/bookService';

type GuruDetailRouteProp = RouteProp<
  { GuruDetail: { guruId: string; guruName: string; guruImage: any; bookIds: string[] } },
  'GuruDetail'
>;

const GuruDetailScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<GuruDetailRouteProp>();
  const { guruName, guruImage, bookIds } = route.params;

  const [books, setBooks] = useState<FireBook[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBooks();
  }, []);

  const loadBooks = async () => {
    setLoading(true);
    const loadedBooks: FireBook[] = [];
    
    for (const bookId of bookIds) {
      try {
        const result = await BookService.getBookDetail(bookId, 'en');
        if (result.success && result.data?.book) {
          loadedBooks.push(result.data.book);
        }
      } catch (error) {
        console.warn(`Failed to load book ${bookId}:`, error);
      }
    }
    
    setBooks(loadedBooks);
    setLoading(false);
  };

  const handleBookPress = (book: FireBook) => {
    const bookId = book.id.includes('_') ? book.id.split('_')[0] : book.id;
    // @ts-ignore
    navigation.navigate('BookDetail', {
      bookId,
      languageCode: book.languageCode || 'en',
    });
  };

  const handleBack = () => {
    navigation.goBack();
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={handleBack} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={Theme.colors.text} />
        </Pressable>
        <View style={styles.headerContent}>
          <Image
            source={guruImage}
            style={styles.guruImage}
            resizeMode="cover"
          />
          <Text variant="h2" style={styles.headerTitle}>
            {guruName}
          </Text>
          <Text variant="body2" color="secondary" style={styles.subtitle}>
            {books.length} books recommended
          </Text>
        </View>
      </View>

      {/* Books List */}
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={Theme.colors.primary} />
            <Text variant="body2" color="secondary" style={styles.loadingText}>
              Loading recommendations...
            </Text>
          </View>
        ) : (
          <View style={styles.booksGrid}>
            {books.map((book) => (
              <Pressable
                key={book.id}
                style={({ pressed }) => [
                  styles.bookCard,
                  pressed && styles.bookCardPressed,
                ]}
                onPress={() => handleBookPress(book)}
              >
                <Image
                  source={{ uri: book.coverImageUrl || book.coverImageFullUrl }}
                  style={styles.bookCover}
                  resizeMode="cover"
                />
                <Text 
                  variant="body2" 
                  weight="semibold" 
                  numberOfLines={2} 
                  style={styles.bookTitle}
                >
                  {book.title}
                </Text>
                <Text variant="caption" color="secondary" numberOfLines={1}>
                  {book.author}
                </Text>
              </Pressable>
            ))}
          </View>
        )}
        
        <View style={styles.bottomSpacing} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  header: {
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.xl,
    borderBottomWidth: 1,
    borderBottomColor: Theme.colors.border,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginBottom: Theme.spacing.md,
  },
  headerContent: {
    alignItems: 'center',
  },
  guruImage: {
    width: 100,
    height: 100,
    borderRadius: 50,
    marginBottom: Theme.spacing.md,
    borderWidth: 3,
    borderColor: Theme.colors.primary,
  },
  headerTitle: {
    textAlign: 'center',
    marginBottom: Theme.spacing.xs,
  },
  subtitle: {
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: Theme.spacing.lg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: Theme.spacing.xxxl,
  },
  loadingText: {
    marginTop: Theme.spacing.md,
  },
  booksGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Theme.spacing.lg,
  },
  bookCard: {
    width: '47%',
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.lg,
    padding: Theme.spacing.md,
    ...Theme.shadows.sm,
  },
  bookCardPressed: {
    opacity: 0.7,
  },
  bookCover: {
    width: '100%',
    aspectRatio: 2 / 3,
    borderRadius: Theme.borderRadius.md,
    marginBottom: Theme.spacing.sm,
  },
  bookTitle: {
    marginBottom: 4,
  },
  bottomSpacing: {
    height: Theme.spacing.xxl,
  },
});

export default GuruDetailScreen;

